import { apiSlice } from "./apiSlice";
import {
  RestoreResponse,
  TrashEntity,
  TrashListResponse,
} from "../../services/trash/trash.types";

export interface GetTrashArgs {
  entity: TrashEntity;
  page: number;
  limit: number;
  days: number;
}

export interface RestoreTrashArgs {
  entity: TrashEntity;
  id: number;
}

/**
 * Trash list + restore. The whole arg (entity/page/limit/days) is the cache
 * key, so a slow response for an old tab/period can never overwrite a newer
 * one (the anti-race guard the old requestIdRef version implemented by hand).
 */
export const trashApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTrash: builder.query<TrashListResponse, GetTrashArgs>({
      query: ({ entity, page, limit, days }) => ({
        url: "/admin/content/trash",
        method: "GET",
        params: { entity, page, limit, days },
      }),
      providesTags: (result, error, { entity }) => [
        { type: "Trash" as const, id: entity },
        { type: "Trash" as const, id: "LIST" },
      ],
    }),
    restoreTrashEntity: builder.mutation<RestoreResponse, RestoreTrashArgs>({
      query: ({ entity, id }) => ({
        url: `/admin/content/trash/${entity}/${id}/restore`,
        method: "POST",
      }),
      // Restoring can change other tabs too (appears_in references), so every
      // Trash tag is invalidated. Content lists refresh through their own
      // tags/paths (see the delete-undo util) — do not over-invalidate here.
      // The error guard matters: RTK Query evaluates invalidatesTags on
      // rejected mutations too, and a failed restore must not refetch.
      invalidatesTags: (result, error) => (error ? [] : ["Trash"]),
    }),
  }),
});

export const { useGetTrashQuery, useRestoreTrashEntityMutation } = trashApi;
